<template>
  <div class="h-100 d-flex flex-column">
    <div class="chart-header">
      <template v-for="sensorCode in allSensorCodes">
        <v-menu
          v-if="sensorStates[sensorCode].enabled"
          :close-on-content-click="false"
        >
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              :prepend-icon="sensorSpecs[sensorCode].icon"
              variant="outlined"
              rounded="pill"
              :color="sensorStates[sensorCode].color"
              class="ma-2"
            >
              {{ getSensorName(sensorCode) }}
              {{ sensorStates[sensorCode].value?.toFixed(3) }}
              {{ sensorUnitInfo[sensorCode]?.displaySymbol }}
            </v-btn>
          </template>
          <v-list class="chart-menu">
            <v-list-item>
              <v-row class="py-5 px-3">
                <ChartDataEnable
                  :chartid="chartid"
                  label="Enabled"
                  :sensor-code="sensorCode"
                  :sensor-state="sensorStates[sensorCode]"
                  @update-enabled="updateEnabled"
                />
                <v-tooltip text="Default Color" location="start">
                  <template #activator>
                    <v-btn
                      variant="text"
                      icon="restore"
                      :color="sensorSpecs[sensorCode].defaultColor"
                      @click="
                        () =>
                          updateColor(
                            sensorCode,
                            sensorSpecs[sensorCode].defaultColor,
                          )
                      "
                    />
                  </template>
                </v-tooltip>
              </v-row>
              <v-color-picker v-model="sensorStates[sensorCode].color" />
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </div>
    <div class="flex-grow-1" />
    <div class="chart-container">
      <Chart
        :before-update="limitZoom"
        :chartid="chartid"
        :datasets="chartDatasets"
        :labels="chartLabels"
        :min-time="minTime"
        :max-time="maxTime"
        :min-val="minVal"
        :max-val="maxVal"
        :max-zoom="maxZoom"
        :show-dots="showDots"
        @contextmenu="onContextMenu"
      />
      <v-menu
        v-model="isContextMenuVisible"
        :close-on-content-click="false"
        :style="contextStyle"
        @mouseleave="isContextMenuVisible = false"
      >
        <v-card>
          <DisplayOptions
            :show-dots="showDots"
            :autoscaling="autoscaling"
            :min-y="minY"
            :max-y="maxY"
            @change-show-dots="changeShowDots"
            @change-scaling="changeScaling"
          />
        </v-card>
      </v-menu>
    </div>
    <v-row class="chart-footer ma-2 flex-grow-0">
      <ChartSettings
        :sensor-states="sensorStates"
        :show-dots="showDots"
        :autoscaling="autoscaling"
        :min-y="minY"
        :max-y="maxY"
        @change-show-dots="changeShowDots"
        @change-scaling="changeScaling"
        @chart-update="chartUpdate"
      />
      <v-menu :close-on-content-click="false" bottom right>
        <template #activator="{ props }">
          <v-btn icon="toggle_on" variant="text" v-bind="props"></v-btn>
        </template>
        <v-list>
          <v-list-item v-for="sensorCode in allSensorCodes" class="pl-2">
            <ChartDataEnable
              :chartid="chartid"
              :label="getSensorName(sensorCode)"
              :sensor-code="sensorCode"
              :sensor-state="sensorStates[sensorCode]"
              @update-enabled="updateEnabled"
            />
          </v-list-item>
        </v-list>
      </v-menu>
      <v-spacer />
      <v-label class="mr-5">{{ clockString }}</v-label>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref, watch } from "vue";
import { ChartDataset, LinearScaleOptions, Scale } from "chart.js/auto";
import "chartjs-adapter-date-fns";
import { millisecondsInSecond } from "date-fns/constants";
import Chart from "../Chart.vue";
import ChartDataEnable from "./ChartDataEnable.vue";
import ChartSettings from "./ChartSettings.vue";
import DisplayOptions from "./DisplayOptions.vue";
import { useModbusAddress } from "@/composables/useModbusAddress";
import { useSensorUnits } from "@/composables/useSensorUnits";
import { useSensorNames } from "@/composables/useSensorNames";
import { useStorage } from "@/composables/useStorage";
import { ChartDataSource } from "@/enums/ChartDataSource";
import { AccessLevel } from "@/enums/AccessLevel";
import { PortState } from "@/enums/PortState";
import { useModbusStore } from "@/stores/useModbusStore";
import { Logger } from "@/types/Logger";
import { DataPoint, valueIndex } from "@/types/graph/DataPoint";
import { SensorCode, isSensorCode } from "@/types/sensor/SensorCode";
import { SetpointValues } from "@/types/SetpointValues";
import { TimerId } from "@/types/TimerId";
import { Timestamp } from "@/types/Timestamp";
import { allSensorCodes, sensorSpecs } from "@/lib/sensors";

const $log = inject<Logger>("vuejs3-logger");

const { modbusAddress } = useModbusAddress();
const modbusStore = useModbusStore();
const { getSensorName } = useSensorNames();

const props = defineProps<{
  chartid: string;
}>();

const {
  getSensorStates,
  showDotsKey,
  timespanKey,
  autoscalingKey,
  minYKey,
  maxYKey,
  enabledKeys,
  colorKeys,
} = useStorage("charts_page", props.chartid);

const { sensorUnitInfo, convertUnit } = useSensorUnits();

// ----
// Turn off limitZoom functionality for the time being:
// The current implementation of PCS-42 is not agreed on by PCS
const USE_LIMIT_ZOOM = false;

const sensorStates = ref(getSensorStates());

const chartLabels = ref<Timestamp[]>([]);
const chartDatasets = ref<ChartDataset[]>(initChartDataSets());

const chartUpdateIntervalMs = ref(100);
const lastChartUpdateTimestamp = ref(Date.now());

const showDots = ref(false);
const autoscaling = ref(true);
const minY = ref(0);
const maxY = ref(100);
const maxZoom = ref(10);

const clockTimer = ref<TimerId>(0);
const clockString = ref(new Date().toLocaleString());

const isContextMenuVisible = ref(false);
const contextMenuX = ref(0);
const contextMenuY = ref(0);

const minTime = ref<Timestamp>(0);
const maxTime = ref<Timestamp>(0);

const pollingFlag = computed<boolean>(() => modbusStore.pollingFlag);
const demoFlag = computed<boolean>(() => modbusStore.demoFlag);

const setpointValues = computed<SetpointValues | undefined>(() =>
  getSetpointRange(),
);

const minVal = computed<number | undefined>(() =>
  !autoscaling.value ? minY.value : undefined,
);
const maxVal = computed<number | undefined>(() =>
  !autoscaling.value ? maxY.value : undefined,
);

const contextStyle = computed<string>(
  () =>
    `position: absolute; z-index: 500; left: ${contextMenuX.value}px; top: ${contextMenuY.value}px;`,
);

const isEqDaConnected = computed<boolean>(
  () => modbusStore.storeInit && modbusStore.portState === PortState.connected,
);

function changeShowDots(state: boolean) {
  showDots.value = state;
  // continues in watcher
}

function changeScaling(
  newAutoscaling: boolean,
  newMinY: number,
  newMaxY: number,
) {
  autoscaling.value = newAutoscaling;
  minY.value = newMinY;
  maxY.value = newMaxY;
  // continues in watcher
}

function chartUpdate() {
  const dataSource = modbusStore.isPollingMode
    ? ChartDataSource.polling
    : modbusStore.isDemoMode
      ? ChartDataSource.demo
      : ChartDataSource.none;

  let nrDataPoints: number;

  if (dataSource === ChartDataSource.none) {
    nrDataPoints = 0;
    maxTime.value = Date.now();
  } else {
    const timestamps: Timestamp[] = modbusStore.labelSeries[dataSource];
    nrDataPoints = timestamps.length;
    maxTime.value = timestamps[nrDataPoints - 1] ?? Date.now();
  }

  const timespan = localStorage.getItem(timespanKey.value) ?? "10";
  minTime.value =
    new Date(maxTime.value).getTime() -
    parseInt(timespan) * millisecondsInSecond;

  if (nrDataPoints < 1) {
    for (let i = 0; i < chartDatasets.value.length; i++) {
      chartDatasets.value[i].data = [];
    }
    return;
  }

  let timeVal = 0;
  let start = 0;
  for (let i = nrDataPoints - 1; i >= 0; i--) {
    if (dataSource !== ChartDataSource.none) {
      timeVal = modbusStore.labelSeries[dataSource][i];
    }
    if (timeVal < minTime.value) {
      start = i + 1;
      break;
    }
  }

  if (dataSource == ChartDataSource.none) {
    chartLabels.value = [];
  } else {
    chartLabels.value = modbusStore.labelSeries[dataSource].slice(
      start,
      nrDataPoints,
    );
  }

  // -------- Sensors --------
  allSensorCodes.forEach((currentSensorCode: SensorCode, index: number) => {
    const currentSensorState = sensorStates.value[currentSensorCode];

    chartDatasets.value[index].label = getSensorName(currentSensorCode);
    chartDatasets.value[index].backgroundColor = currentSensorState.color;
    chartDatasets.value[index].borderColor = currentSensorState.color;

    if (!currentSensorState.enabled) {
      chartDatasets.value[index].data = [];
      currentSensorState.value = 0;
      return;
    }
    if (dataSource === ChartDataSource.none) {
      chartDatasets.value[index].data = [];
      currentSensorState.value = 0;
    } else {
      const convertedData = modbusStore.dataSeries[currentSensorCode][
        dataSource
      ]
        .slice(start, nrDataPoints)
        .map((val: DataPoint): number =>
          convertUnit(val[valueIndex], currentSensorCode),
        );
      chartDatasets.value[index].data = convertedData;

      currentSensorState.value = parseFloat(
        convertedData[convertedData.length - 1].toFixed(3),
      );
    }
  });
}

function initChartDataSets(): ChartDataset[] {
  return allSensorCodes.map((sensorCode: SensorCode) => ({
    backgroundColor: sensorStates.value[sensorCode].color,
    borderColor: sensorStates.value[sensorCode].color,
    borderWidth: 1,
    data: [] as DataPoint[],
    label: getSensorName(sensorCode),
  }));
}

function getChartSettings() {
  showDots.value = localStorage.getItem(showDotsKey.value) === "true";
  autoscaling.value = localStorage.getItem(autoscalingKey.value) === "true";
  minY.value = parseFloat(localStorage.getItem(minYKey.value) ?? "0");
  maxY.value = parseFloat(localStorage.getItem(maxYKey.value) ?? "100");
}

function updateEnabled(sensorCode: SensorCode, enabled: boolean) {
  sensorStates.value[sensorCode].enabled = enabled;
  localStorage.setItem(enabledKeys.value[sensorCode], String(enabled));
  chartUpdate();
}

function updateColor(sensorCode: SensorCode, color: string) {
  sensorStates.value[sensorCode].color = color;
  localStorage.setItem(colorKeys.value[sensorCode], color);
  chartUpdate();
}

function getSetpointRange(): SetpointValues | undefined {
  if (!isEqDaConnected.value) {
    return;
  }
  const min = modbusStore.modbusAddrValue[modbusAddress.setpointManagerMin];
  const max = modbusStore.modbusAddrValue[modbusAddress.setpointManagerMax];
  const actual = modbusStore.modbusAddrValue[modbusAddress.actualSetpoint];
  $log?.debug(`SetpointRange: min: ${min} max: ${max} actual: ${actual}`);
  if (
    typeof min !== "number" ||
    typeof max !== "number" ||
    typeof actual !== "number"
  ) {
    return;
  }
  return { min, max, actual };
}

/**
 * Check if we don't exceed the maximum zoom factor.
 * Note: this check is only performed in non-full-access mode and when autoscaling is on.
 * Also, a maximum zoom level must have been set (`maxZoom` must not be zero)
 *
 * If the zoom level is too high (i.e. when the graph range is too small), we
 * rescale the vertical axis.
 */
function limitZoom(scale: Scale<LinearScaleOptions>) {
  if (!USE_LIMIT_ZOOM) {
    return;
  }
  if (!isEqDaConnected.value) {
    $log?.debug("limitZoom: not connected");
    return;
  }
  if (modbusStore.accessLevel >= AccessLevel.full) {
    $log?.debug("limitZoom: no restrictions in full mode and up");
    return;
  }
  if (maxZoom.value === 0) {
    $log?.debug("limitZoom: no maxZoom configured");
    return;
  }
  if (!autoscaling.value) {
    $log?.debug("limitZoom: autoscaling is off");
    return;
  }
  if (!setpointValues.value) {
    $log?.debug("limitZoom: no setpointvalues");
    return;
  }
  const currentMinMax = scale.getMinMax(false);
  const currentRange = currentMinMax.max - currentMinMax.min;
  if (currentRange === Infinity || currentRange === -Infinity) {
    $log?.debug("limitZoom: current range is infinite");
    return;
  }

  const rangeMidpoint = currentMinMax.min + currentRange / 2;
  // The maximum zoom factor corresponds to a certain minimum value range.
  const minRange =
    (setpointValues.value.max - setpointValues.value.min) / maxZoom.value;
  if (currentRange >= minRange) {
    // This zoom level is acceptable
    $log?.debug(
      `limitZoom: the zoom level is acceptable (${currentRange} >= ${minRange})`,
    );
    return;
  }
  // This zoom level is too high: adjust it
  $log?.debug(
    `limitZoom: adjusting zoom level to ${rangeMidpoint} ± ${minRange / 2}`,
  );
  scale.options.min = rangeMidpoint - minRange / 2;
  scale.options.max = rangeMidpoint + minRange / 2;
}

function onContextMenu(event: MouseEvent): void {
  const scrollY =
    document.getElementById("main_div")?.getBoundingClientRect().top ?? 0;
  event.preventDefault();
  contextMenuX.value = event.clientX - 5;
  contextMenuY.value = event.clientY - scrollY + 60;
  isContextMenuVisible.value = true;
}

//region **** [[watchers]] **************************************************

watch(isContextMenuVisible, () => {
  getChartSettings();
});

watch([pollingFlag, demoFlag], () => {
  getChartSettings();
  const now = Date.now();
  if (now - lastChartUpdateTimestamp.value > chartUpdateIntervalMs.value) {
    chartUpdate();
  }
});

watch(showDots, (newVal: boolean) => {
  localStorage.setItem(showDotsKey.value, String(newVal));
  chartUpdate();
});

watch(
  [autoscaling, minY, maxY],
  ([newAutoscaling, newMinY, newMaxY]: [boolean, number, number]) => {
    localStorage.setItem(autoscalingKey.value, String(newAutoscaling));
    localStorage.setItem(minYKey.value, String(newMinY));
    localStorage.setItem(maxYKey.value, String(newMaxY));
    chartUpdate();
  },
);

watch(
  () => ({
    RS: sensorStates.value.RS.color,
    ISA: sensorStates.value.ISA.color,
    ISB: sensorStates.value.ISB.color,
    ISC: sensorStates.value.ISC.color,
    SP: sensorStates.value.SP.color,
    IV: sensorStates.value.IV.color,
    EV: sensorStates.value.EV.color,
    IEV: sensorStates.value.IEV.color,
    DT: sensorStates.value.DT.color,
  }),
  (
    newVals: Record<SensorCode, string>,
    oldVals: Record<SensorCode, string>,
  ) => {
    Object.keys(newVals).forEach((key: string) => {
      if (isSensorCode(key) && newVals[key] !== oldVals[key]) {
        localStorage.setItem(colorKeys.value[key], newVals[key]); // Alleen bij wijziging
        chartUpdate();
      }
    });
  },
  { deep: true },
);
//endregion

onMounted(() => {
  sensorStates.value = getSensorStates();
  getChartSettings();
  chartUpdate();

  clockTimer.value = setInterval(async () => {
    clockString.value = new Date().toLocaleString();
  }, millisecondsInSecond);
});

onUnmounted(() => {
  if (clockTimer.value) {
    clearInterval(clockTimer.value);
  }
});
</script>

<style scoped lang="scss">
.chart-header {
  .chart-menu {
    top: 50%;
    left: 50%;
  }
}
</style>
